import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { eur } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CourseFilters, MODALITY_LABELS, filterCourses, filterLabel, downloadCoursesPdf } from "@/components/app/CourseFilters";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { ShoppingCart, Eye, FileDown, Loader2 } from "lucide-react";

function PriceCell({ c }) {
  const special = c.partner_discount_percent > 0 || c.partner_custom_price;
  return (
    <div className="whitespace-nowrap">
      <span className="font-semibold text-[#0F2942]" data-testid={`course-price-${c.id}`}>{eur(c.partner_price_final ?? c.partner_price)}</span>
      {special && (
        <Badge className="ml-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`course-discount-${c.id}`}>
          {c.partner_custom_price ? "Prezzo dedicato" : `-${c.partner_discount_percent}%`}
        </Badge>
      )}
    </div>
  );
}

export default function Catalog() {
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [mod, setMod] = useState("all");
  const [exporting, setExporting] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();
  const addToCart = (c) => {
    if (c.modality !== "videoconferenza") return addItem(c);
    toast.info("Scegli l'edizione in programma nella scheda del corso");
    navigate(`/partner/corsi/${c.id}`);
  };

  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)); }, []);
  const categories = useMemo(() => [...new Set(courses.map((c) => c.category).filter(Boolean))].sort(), [courses]);
  const filtered = useMemo(() => filterCourses(courses, q, cat, mod), [courses, q, cat, mod]);

  const exportPdf = async () => {
    setExporting(true);
    try { await downloadCoursesPdf("/export/courses.pdf", filtered.map((c) => c.id), filterLabel(q, cat, mod)); }
    catch { toast.error("Esportazione PDF non riuscita"); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="catalog-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Catalogo corsi</h1>
          <p className="text-slate-500 mt-1">Corsi disponibili con il prezzo a te riservato (IVA esclusa).</p>
        </div>
        <Button variant="outline" onClick={exportPdf} disabled={exporting || filtered.length === 0} data-testid="catalog-export-pdf">
          {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />} Esporta PDF
        </Button>
      </div>
      <CourseFilters q={q} setQ={setQ} cat={cat} setCat={setCat} mod={mod} setMod={setMod} categories={categories} count={filtered.length} />
      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table className="min-w-[860px]">
          <TableHeader><TableRow>
            <TableHead>Titolo</TableHead><TableHead>Categoria</TableHead><TableHead>Modalità</TableHead>
            <TableHead>Durata</TableHead><TableHead>Prezzo pubblico</TableHead><TableHead>Prezzo riservato</TableHead><TableHead>IVA</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`course-row-${c.id}`}>
                <TableCell className="font-medium"><Link to={`/partner/corsi/${c.id}`} className="hover:text-amber-600">{c.title}</Link></TableCell>
                <TableCell>{c.category}</TableCell>
                <TableCell>{MODALITY_LABELS[c.modality] || c.modality}</TableCell>
                <TableCell>{c.duration_hours}h</TableCell>
                <TableCell className="text-slate-500 whitespace-nowrap" data-testid={`course-public-price-${c.id}`}>{c.public_price ? eur(c.public_price) : "—"}</TableCell>
                <TableCell><PriceCell c={c} /></TableCell>
                <TableCell><span className={`status-pill ${(c.vat_rate ?? 22) === 0 ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`}>{c.vat_rate ?? 22}%</span></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button asChild variant="ghost" size="icon" title="Scheda corso" data-testid={`course-detail-${c.id}`}>
                    <Link to={`/partner/corsi/${c.id}`}><Eye className="w-4 h-4" /></Link>
                  </Button>
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white ml-1" onClick={() => addToCart(c)} data-testid={`add-to-cart-${c.id}`}>
                    <ShoppingCart className="w-4 h-4 mr-1" /> Carrello
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-12">Nessun corso trovato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
